import { useState, useEffect, useCallback } from 'react';
import { bannerService } from '../services';
import type { Banner } from '../types';

interface UseBannerRotationOptions {
  /** Intervalo de rotação em milissegundos (padrão: 15 segundos) */
  rotationInterval?: number;
}

interface UseBannerRotationReturn {
  /** Banners normais (pool geral) */
  normalBanners: Banner[];
  /** Banners fullwidth */
  fullwidthBanners: Banner[];
  /** Se está carregando */
  loading: boolean;
  /** Pega N banners de uma lista com rodízio automático */
  pickFromPool: (list: Banner[], slotIndex: number, count?: number) => Banner[];
  /** Registra clique */
  trackClick: (bannerId: number) => void;
  /** Registra impressão */
  trackImpression: (bannerId: number) => void;
}

/**
 * Hook para gerenciar banners com rodízio automático.
 *
 * Busca banners por tipo (normal e fullwidth) via API.
 * O backend retorna os banners já em ordem aleatória.
 * O hook adiciona rotação temporal para que os banners
 * visíveis mudem a cada intervalo.
 */
export function useBannerRotation(options: UseBannerRotationOptions = {}): UseBannerRotationReturn {
  const { rotationInterval = 15000 } = options;

  const [normalBanners, setNormalBanners] = useState<Banner[]>([]);
  const [fullwidthBanners, setFullwidthBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [rotationOffset, setRotationOffset] = useState(() => Math.floor(Math.random() * 100));

  // Carrega banners por tipo
  const loadBanners = useCallback(async () => {
    try {
      setLoading(true);
      // Busca banners por tipo (novos: home/vagas, legado: normal/fullwidth)
      const [home, vagas, normal, fullwidth] = await Promise.all([
        bannerService.getBannersByType('home').catch(() => []),
        bannerService.getBannersByType('vagas').catch(() => []),
        bannerService.getBannersByType('normal').catch(() => []),
        bannerService.getBannersByType('fullwidth').catch(() => []),
      ]);
      // Combina banners novos + legados
      const allHome = [...(Array.isArray(home) ? home : []), ...(Array.isArray(normal) ? normal : []), ...(Array.isArray(fullwidth) ? fullwidth : [])];
      const allVagas = Array.isArray(vagas) ? vagas : [];
      setNormalBanners(allHome);
      setFullwidthBanners(allVagas);
    } catch (error) {
      console.error('Erro ao carregar banners:', error);
      setNormalBanners([]);
      setFullwidthBanners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  // Rotação automática
  useEffect(() => {
    const maxLen = Math.max(normalBanners.length, fullwidthBanners.length);
    if (maxLen <= 1) return;

    const interval = setInterval(() => {
      setRotationOffset((prev) => prev + 1);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [normalBanners.length, fullwidthBanners.length, rotationInterval]);

  /**
   * Pega N banners de uma lista com rodízio.
   * Usa apenas rotationOffset para avançar a janela,
   * garantindo que todos os banners apareçam ciclicamente.
   */
  const pickFromPool = useCallback((list: Banner[], slotIndex: number, count: number = 2): Banner[] => {
    if (!list || list.length === 0) return [];
    const len = list.length;
    const result: Banner[] = [];
    const usedIds = new Set<number>();
    const start = (slotIndex * count + rotationOffset) % len;
    for (let i = 0; i < Math.min(count, len); i++) {
      let idx = (start + i) % len;
      // Evita duplicatas se a lista for pequena
      let attempts = 0;
      while (usedIds.has(list[idx].id) && attempts < len) {
        idx = (idx + 1) % len;
        attempts++;
      }
      usedIds.add(list[idx].id);
      result.push(list[idx]);
    }
    return result;
  }, [rotationOffset]);

  const trackClick = useCallback((bannerId: number) => {
    bannerService.trackClick(bannerId).catch(console.error);
  }, []);

  const trackImpression = useCallback((bannerId: number) => {
    bannerService.trackImpression(bannerId).catch(console.error);
  }, []);

  return {
    normalBanners,
    fullwidthBanners,
    loading,
    pickFromPool,
    trackClick,
    trackImpression,
  };
}
